/**
 * Class to implement the teaser functionality
 * for a single instance; the application is done
 * via the TeaserManager class
 */
export class Teaser {
    private grid: HTMLElement | null = null;
    private articles: NodeListOf<HTMLElement> | null = null;

    /**
     * Constructor of the Teaser
     *
     * @param {HTMLElement} teaser
     */
    public constructor(private teaser: HTMLElement) {
        // Select the grid element
        this.grid = teaser.querySelector(":scope > div");
        if (!this.grid) return;

        // We only want to apply the logic for complete
        // image-coverage and equal content-height
        if (this.grid.getAttribute("image-coverage") !== "complete") return;
        if (this.grid.getAttribute("content-height") !== "equal") return;

        // Select the article elements
        this.articles = teaser.querySelectorAll(`article`);
        if (!this.articles.length) return;

        // Create the teaser
        this.instantiate();
    }

    /**
     * Instantiates the functionality of the teaser
     *
     * @returns {void}
     */
    private instantiate(): void {
        // Listen to resize
        window.addEventListener("resize", this.evaluate, {
            passive: true,
        });

        // Initial evaluation
        this.evaluate();
    }

    /**
     * Method to handle the resize-event;
     * equalizes the height of all content-boxes with the same
     * content-position within a row but only for those teasers,
     * that are configured with image-coverage "complete" and
     * content-height "equal"
     *
     * @returns {void}
     */
    private evaluate = (): void => {
        const articles = Array.from(this.articles ?? []);
        if (!articles.length) return;

        const rows = new Map<number, Record<"start" | "end", HTMLElement[]>>();

        articles.forEach((article) => {
            const content = article.querySelector(
                ":scope > div",
            ) as HTMLElement | null;
            if (content) content.style.height = "";
        });

        articles.forEach((article) => {
            const content = article.querySelector(
                ":scope > div",
            ) as HTMLElement | null;
            const position = article.getAttribute("content-position");

            if (!content || (position !== "start" && position !== "end"))
                return;

            const rowTop = Math.round(article.getBoundingClientRect().top);

            if (!rows.has(rowTop)) {
                rows.set(rowTop, { start: [], end: [] });
            }

            rows.get(rowTop)![position].push(content);
        });

        rows.forEach((group) => {
            (["start", "end"] as const).forEach((position) => {
                const contents = group[position];
                const tallest = contents.reduce(
                    (max, el) => Math.max(max, el.offsetHeight),
                    0,
                );

                contents.forEach((el) => {
                    el.style.height = `${tallest}px`;
                });
            });
        });
    };
}
